import { useEffect, useState } from 'react';

/**
 * MM-1172: session flag asking for the patient picker, set when the guardian has more than one child
 * profile, either at sign-in or after re-entering the PIN following an idle timeout. It lives in
 * sessionStorage so a refresh keeps the picker up until a child is chosen, and is wiped with the rest
 * of the session on logout. Its value is the reason the picker was asked for.
 */
const PATIENT_SELECTION_PENDING_KEY = 'patientSelectionPending';

/**
 * MM-1172: set when the idle timer fires. Kept apart from localStorage 'onIdle', which the
 * tab-visible PIN check overwrites with '2' when the guardian comes back to an idle tab.
 */
const IDLE_TIMED_OUT_KEY = 'idleTimedOut';

/** Window event fired whenever the pending flag changes, so open screens follow it. */
const PATIENT_SELECTION_CHANGE_EVENT = 'patientselectionchange';

/**
 * Why the picker is showing:
 * signIn - right after sign-in; the choice always lands on the dashboard with the "Now viewing" toast
 * idle   - after the PIN following an idle timeout; keeping the open child stays on the current page
 */
export type PatientSelectionReason = 'signIn' | 'idle';

/** Asks for the patient picker. */
export const markPatientSelectionPending = (reason: PatientSelectionReason): void => {
  sessionStorage.setItem(PATIENT_SELECTION_PENDING_KEY, reason);
  window.dispatchEvent(new Event(PATIENT_SELECTION_CHANGE_EVENT));
};

/** Why the picker is pending, or null when it is not. */
export const getPatientSelectionReason = (): PatientSelectionReason | null => {
  const reason = sessionStorage.getItem(PATIENT_SELECTION_PENDING_KEY);
  return reason === 'signIn' || reason === 'idle' ? reason : null;
};

/** Whether the guardian still has to pick which child to open. */
export const isPatientSelectionPending = (): boolean => getPatientSelectionReason() !== null;

/** Marks the picker as done. */
export const clearPatientSelectionPending = (): void => {
  sessionStorage.removeItem(PATIENT_SELECTION_PENDING_KEY);
  window.dispatchEvent(new Event(PATIENT_SELECTION_CHANGE_EVENT));
};

/** Records that the idle timer fired, so a correct PIN can bring up the picker. */
export const markIdleTimedOut = (): void => {
  sessionStorage.setItem(IDLE_TIMED_OUT_KEY, 'yes');
};

/** Whether the idle timer fired since the last PIN; clears the record. */
export const consumeIdleTimedOut = (): boolean => {
  const timedOut = sessionStorage.getItem(IDLE_TIMED_OUT_KEY) === 'yes';
  sessionStorage.removeItem(IDLE_TIMED_OUT_KEY);
  return timedOut;
};

/**
 * Whether the patient picker is pending, kept in step with the session flag.
 */
export const usePatientSelectionPending = (): boolean => {
  const [pending, setPending] = useState(isPatientSelectionPending);

  useEffect(() => {
    const onChange = () => setPending(isPatientSelectionPending());
    window.addEventListener(PATIENT_SELECTION_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(PATIENT_SELECTION_CHANGE_EVENT, onChange);
  }, []);

  return pending;
};
